// React and Next.js imports
import type { GetStaticProps, GetStaticPaths } from "next";
import "@/registry/builder-registry";
import type { BasePageProps } from "@orlandohealth/build-kit";
import { GenericPage } from "@orlandohealth/build-kit";
import { GenericWrapper } from "@orlandohealth/build-kit";
import { logger } from "@orlandohealth/utils";
import { getAllBuilderContent } from "@orlandohealth/build-kit";


// Explicitly pass GenericWrapper as the wrapper component.  This is the default wrapper so
// it's not necessary to pass it but providing it here for clarity.
const Page = GenericPage.createPage(GenericWrapper);

// Revalidate window for not-found results. Kept high so that crawlers/scanners
// repeatedly hitting nonexistent URLs don't trigger a fresh on-demand render on
// every request (the previous 1s value caused 404 "storms" that inflated
// Vercel Function Duration). Successful pages use AbstractPage's
// ISR_REVALIDATE_SECONDS-driven window instead.
const NOT_FOUND_REVALIDATE = 300;

export const getStaticProps: GetStaticProps<BasePageProps> = async ({
  params,
  locale,
  defaultLocale,
  preview,
  previewData = {},
}) => {
  let urlPath = "";
  try {
    // Check for test error parameter
    urlPath =
      "/" +
      (Array.isArray(params?.page)
        ? params.page.join("/")
        : params?.page || "");

    logger.info(`Processing page: ${urlPath}`, "static-props");

    if (!isPlausibleBuilderPageUrl(urlPath)) {
      logger.warn(
        `Rejecting non-path URL for static props: ${urlPath.slice(0, 160)}${urlPath.length > 160 ? "…" : ""}`,
        "static-props"
      );
      return { notFound: true, revalidate: NOT_FOUND_REVALIDATE };
    }

    // Check if we're in Builder by looking at previewData
    const isInBuilder =
      !!(previewData as any)?.["builder.space"] ||
      !!(previewData as any)?.["builder.frameEditing"];

    logger.info(`Fetching content for page: ${urlPath}`, "static-props");
    // For other pages, try to get the content
    const result = await GenericPage.getStaticProps(
      urlPath,
      locale,
      defaultLocale,
      "page",
      preview || isInBuilder
    );

    logger.info(`Content fetched for page: ${urlPath}`, "static-props");

    // If no content was found and we're not in preview mode, return notFound
    if (!result.props.content && !preview && !isInBuilder) {
      logger.info(`No content found for page: ${urlPath}`, "static-props");
      return {
        notFound: true,
        revalidate: NOT_FOUND_REVALIDATE,
      };
    }

    logger.info(`Successfully processed page: ${urlPath}`, "static-props");
    return {
      ...result,
      props: {
        ...result.props,
        locale,
        defaultLocale,
      },
    };
  } catch (error: unknown) {
    logger.error(`Error processing page ${urlPath}: ${error}`, "static-props");
    return {
      notFound: true,
      revalidate: NOT_FOUND_REVALIDATE,
    };
  }
};

// You might want to move these to a config file like config/routing.ts
const EXCLUDED_DIRECTORIES = [
  "/services",
  "/physician-finder",
  "/clinical-trials",
  "/content-hub",
  "/search",
  "/dynamiclink",
  "/section-editors",
  "/medical-professionals/the-update",
  "/facilities",
];

// Helper function to check if a path should be excluded
const shouldExcludePath = (url: string): boolean => {
  // Check if the path starts with any of the excluded directories (case-insensitive)
  const lowerUrl = url.toLowerCase();
  const isExcludedDirectory = EXCLUDED_DIRECTORIES.some((dir) =>
    lowerUrl.startsWith(dir.toLowerCase())
  );

  // Also exclude standalone pages that have their own implementations
  const standalonePages = ["/404", "/error", "/500"];
  const isStandalonePage = standalonePages.includes(url);

  return isExcludedDirectory || isStandalonePage || url === "/";
};

const MAX_BUILDER_PAGE_URL_LENGTH = 2048;
const MAX_PATH_SEGMENT_LENGTH = 255;

/** Rejects URLs that are not real paths (e.g. serialized Builder element JSON in data.url). */
function isPlausibleBuilderPageUrl(url: string): boolean {
  const trimmed = url.trim();
  if (!trimmed.startsWith("/")) {
    return false;
  }
  if (trimmed.length > MAX_BUILDER_PAGE_URL_LENGTH) {
    return false;
  }
  if (trimmed.startsWith("/[") || trimmed.includes("@builder.io/sdk")) {
    return false;
  }
  const segments = trimmed.split("/").filter(Boolean);
  for (const segment of segments) {
    if (segment.length > MAX_PATH_SEGMENT_LENGTH) {
      return false;
    }
  }
  return true;
}

export const getStaticPaths: GetStaticPaths = async ({ locales }) => {
  if (process.env.SKIP_SSG === "true") {
    return { paths: [], fallback: "blocking" };
  }
  try {
    logger.info("Fetching pages from Builder.io with pagination...", "static-paths");
    const startTime = Date.now();
    
    const pages = await getAllBuilderContent("page", {
      fields: "data.url",
      options: { noTargeting: true },
      cachebust: true,
    });

    const fetchTime = Date.now() - startTime;
    logger.debug(`Fetched ${pages.length} pages in ${fetchTime}ms`, "static-paths");

    const paths = pages.flatMap((page) => {
      const url = String(page.data?.url ?? "").trim();

      if (!isPlausibleBuilderPageUrl(url)) {
        logger.warn(
          `Skipping page with invalid URL (not a static path): ${url.slice(0, 160)}${url.length > 160 ? "…" : ""}`,
          "static-paths"
        );
        return [];
      }

      const isValid = url !== "/" && !shouldExcludePath(url);
      if (!isValid) {
        logger.debug(`Excluding page: ${url}`, "static-paths");
        if (url === "/") {
          logger.debug("Reason: Root path", "static-paths");
        } else if (shouldExcludePath(url)) {
          if (EXCLUDED_DIRECTORIES.some((dir) => url.startsWith(dir))) {
            logger.debug("Reason: Matches excluded directory", "static-paths");
          } else {
            logger.debug("Reason: Is a standalone page", "static-paths");
          }
        }
        return [];
      }

      // Normalize URL to lowercase to ensure consistent file naming
      // This prevents case mismatch issues during static export on case-insensitive filesystems
      const normalizedUrl = url.toLowerCase();
      return (locales || []).map((locale) => ({
        params: {
          page: normalizedUrl.split("/").filter(Boolean),
        },
        locale,
      }));
    });

    logger.debug(
      `Generated ${paths.length} paths for static generation`,
      "static-paths"
    );
    
    // Log first few paths for debugging
    const samplePaths = paths.slice(0, 5);
    samplePaths.forEach((path, index) => {
      logger.debug(
        `Sample ${index + 1}. /${path.params.page.join("/")} (locale: ${path.locale})`,
        "static-paths"
      );
    });

    if (paths.length === 0) {
      logger.warn(
        "No static paths generated! This will cause all pages to use fallback rendering, which may fail during Builder.io outages.",
        "static-paths"
      );
    }

    return {
      paths,
      // Use "blocking" fallback to handle any pages not pre-generated
      // This provides a safety net during Builder.io outages
      fallback: "blocking",
    };
  } catch (error) {
    logger.error(`Critical error in getStaticPaths: ${error}`, "static-paths");
    logger.error(
      "Returning empty paths - all pages will use fallback rendering. This increases risk during Builder.io outages.",
      "static-paths"
    );
    
    return {
      paths: [],
      fallback: "blocking",
    };
  }
};

export default Page;
